import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email.trim().toLowerCase(), password, businessName.trim());
      toast.success("Account created");
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
        <BrandMark size="lg" to="/" testid="register-brand" className="mb-6 gap-3" withTagline />
        <h1 className="text-2xl font-bold tracking-tight">Open your vendor portal</h1>
        <p className="text-sm text-slate-500 mt-1">Takes less than a minute.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="biz">Business name</Label>
            <Input
              id="biz"
              required
              minLength={1}
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              className="focus:ring-[#E8650A] focus:border-[#E8650A]"
              data-testid="register-business-input"
            />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="focus:ring-[#E8650A] focus:border-[#E8650A]"
              data-testid="register-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="focus:ring-[#E8650A] focus:border-[#E8650A]"
              data-testid="register-password-input"
            />
            <p className="text-xs text-slate-500 mt-1">Minimum 6 characters.</p>
          </div>
          <Button type="submit" disabled={loading} className="ff-btn-primary w-full" data-testid="register-submit-button">
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Create account
          </Button>
        </form>

        <div className="text-sm text-slate-500 mt-6 text-center">
          Already have one?{" "}
          <Link to="/login" className="text-[#E8650A] font-semibold" data-testid="link-to-login">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
